// Task State Machine - Wiedervorlage (Follow-up) Sistemi
// AgentTasks.jsx'ten çıkarılan görev durumu yönetimi

import { base44 } from '@/api/base44Client';
import { notifyTaskAssigned, notifyTaskCompleted } from '@/components/notifications/notificationHelper';

/**
 * Görev tipleri
 */
export const TASK_TYPES = [
  'Erstbesuch',    // İlk ziyaret (Vertriebsprozess başlangıcı)
  'Besuchen',      // Ziyaret
  'Anrufen',       // Arama
  'Angebot',       // Teklif
  'Nachfassen',    // Takip / Rückmeldeanfrage
  'Lieferung',     // Teslimat
  'Reklamation',   // Şikayet
  'Bedarfsabfrage',// İhtiyaç analizi/Anket
];

/**
 * Bilinmeyen veya alias task tiplerini known tiplere normalize eder.
 * DB'de manuel/legacy oluşturulmuş tipler için (örn. "Befragen" → "Bedarfsabfrage").
 */
export function normalizeTaskType(rawType) {
  if (!rawType) return null;
  const lower = String(rawType).toLowerCase().trim();
  const aliasMap = {
    'befragen': 'Bedarfsabfrage',
    'bedarfsabfrage': 'Bedarfsabfrage',
    'erstbesuch': 'Erstbesuch',
    'erstkontakt': 'Erstbesuch',
    'besuchen': 'Besuchen',
    'besuch': 'Besuchen',
    'visit': 'Besuchen',
    'anrufen': 'Anrufen',
    'anruf': 'Anrufen',
    'call': 'Anrufen',
    'angebot': 'Angebot',
    'offer': 'Angebot',
    'nachfassen': 'Nachfassen',
    'followup': 'Nachfassen',
    'follow-up': 'Nachfassen',
    'lieferung': 'Lieferung',
    'delivery': 'Lieferung',
    'reklamation': 'Reklamation',
    'complaint': 'Reklamation',
  };
  return aliasMap[lower] || rawType; // bilinmeyen değerleri olduğu gibi bırak
}

/**
 * Görev sonuç seçenekleri (outcome)
 */
export const TASK_OUTCOMES = {
  // Vertriebsprozess Schritt 1: Erstbesuch abgeschlossen → Lead-Status
  // wird automatisch auf "Interessenten" gesetzt (siehe updateLeadStatusByOutcome).
  'Erstbesuch': [
    { value: 'erstbesuch_done', label: 'Erstbesuch abgeschlossen', nextTask: null, nextDays: 0 },
    { value: 'not_reached', label: 'Nicht angetroffen', nextTask: 'Erstbesuch', nextDays: 3 },
    { value: 'not_interested', label: 'Nicht interessiert', nextTask: null, nextDays: 0 },
  ],
  'Besuchen': [
    { value: 'interested', label: 'Interessiert', nextTask: 'Angebot', nextDays: 2 },
    { value: 'callback', label: 'Rückruf gewünscht', nextTask: 'Anrufen', nextDays: 3 },
    { value: 'not_interested', label: 'Kein Interesse', nextTask: null, nextDays: 30 },
    { value: 'not_reached', label: 'Nicht erreicht', nextTask: 'Besuchen', nextDays: 7 },
    { value: 'order', label: 'Bestellung aufgenommen', nextTask: 'Lieferung', nextDays: 1 },
  ],
  'Anrufen': [
    { value: 'appointment', label: 'Termin vereinbart', nextTask: 'Besuchen', nextDays: 0 },
    { value: 'send_offer', label: 'Angebot senden', nextTask: 'Angebot', nextDays: 1 },
    { value: 'callback', label: 'Rückruf', nextTask: 'Anrufen', nextDays: 2 },
    { value: 'not_reached', label: 'Nicht erreicht', nextTask: 'Anrufen', nextDays: 1 },
    { value: 'not_interested', label: 'Kein Interesse', nextTask: null, nextDays: 60 },
  ],
  'Angebot': [
    { value: 'sent', label: 'Angebot gesendet', nextTask: 'Nachfassen', nextDays: 3 },
    { value: 'accepted', label: 'Angenommen', nextTask: 'Besuchen', nextDays: 1 },
    { value: 'rejected', label: 'Abgelehnt', nextTask: null, nextDays: 30 },
    { value: 'negotiating', label: 'In Verhandlung', nextTask: 'Anrufen', nextDays: 2 },
  ],
  // Vertriebsprozess Schritt 4: Rückmeldung erfassen.
  // positive        → Status "Bedarfsabfrage" + Bedarfsabfrage-To-do (sofort)
  // needs_time      → Wiedervorlage: "Besuchen" nach 7 Tagen
  // no_response     → "Besuchen" nach 2 Tagen
  // not_interested  → Status "Nicht interessiert", keine weiteren To-dos
  'Nachfassen': [
    { value: 'positive', label: 'Positive Rückmeldung erhalten', nextTask: 'Bedarfsabfrage', nextDays: 0 },
    { value: 'needs_time', label: 'Braucht Zeit', nextTask: 'Besuchen', nextDays: 7 },
    { value: 'no_response', label: 'Keine Rückmeldung', nextTask: 'Besuchen', nextDays: 2 },
    { value: 'not_interested', label: 'Nicht interessiert', nextTask: null, nextDays: 0 },
    { value: 'order', label: 'Bestellung', nextTask: 'Lieferung', nextDays: 1 },
  ],
  'Lieferung': [
    { value: 'delivered', label: 'Geliefert', nextTask: 'Bedarfsabfrage', nextDays: 14 },
    { value: 'rescheduled', label: 'Verschoben', nextTask: 'Lieferung', nextDays: 1 },
    { value: 'problem', label: 'Problem', nextTask: 'Reklamation', nextDays: 0 },
  ],
  'Reklamation': [
    { value: 'resolved', label: 'Gelöst', nextTask: 'Nachfassen', nextDays: 7 },
    { value: 'pending', label: 'In Bearbeitung', nextTask: 'Reklamation', nextDays: 2 },
    { value: 'escalated', label: 'Eskaliert', nextTask: 'Anrufen', nextDays: 1 },
  ],
  'Bedarfsabfrage': [
    { value: 'repeat_task', label: 'Aufgabe wieder', nextTask: 'Bedarfsabfrage', nextDays: 7 },
    { value: 'lost_customer', label: 'Kunde verloren', nextTask: null, nextDays: 0 },
    { value: 'complaint', label: 'Beschwerde', nextTask: 'Reklamation', nextDays: 0 },
    { value: 'needs_order', label: 'Neue Bestellung', nextTask: 'Lieferung', nextDays: 1 },
    { value: 'not_reached', label: 'Nicht erreicht', nextTask: 'Bedarfsabfrage', nextDays: 2 },
  ],
};

/**
 * Görevi tamamlar ve gerekirse Wiedervorlage (follow-up) oluşturur
 * @param {Object} task - Mevcut görev
 * @param {string} outcome - Sonuç değeri
 * @param {string} notes - Notlar
 * @param {Date} customDate - Özel tarih (opsiyonel)
 * @returns {Promise<Object>} Yeni görev veya null
 */
export async function completeTask(task, outcome, notes = '', customDate = null) {
  // Eski kayıtlarda task_type boş olabilir; typ alanını fallback olarak kullan
  // ve alias normalize et (örn. "Befragen" → "Bedarfsabfrage").
  const effectiveType = normalizeTaskType(task.task_type || task.typ);
  const outcomeConfig = effectiveType
    ? TASK_OUTCOMES[effectiveType]?.find(o => o.value === outcome)
    : null;

  // Bilinmeyen tip / generic manuel kapatma → sessiz kapatma (no follow-up).
  // Bu, eski/migrate edilmiş veya tipsiz task'ların kapanmasını sağlar.
  if (!outcomeConfig) {
    await base44.entities.Task.update(task.id, {
      status: 'Abgeschlossen',
      action_result: outcome || 'manual_close',
      ergebnis: 'Manuell abgeschlossen',
      notes: notes,
      completed_date: new Date().toISOString(),
    });
    return null;
  }

  // Mevcut görevi tamamla
  await base44.entities.Task.update(task.id, {
    status: 'Abgeschlossen',
    action_result: outcome,
    ergebnis: outcomeConfig.label,
    notes: notes,
    completed_date: new Date().toISOString(),
  });

  // Wiedervorlage gerekiyorsa yeni görev oluştur
  if (outcomeConfig.nextTask) {
    const nextDate = customDate || calculateNextDate(outcomeConfig.nextDays);

    const newTask = await base44.entities.Task.create({
      lead_id: task.lead_id,
      lead_name: task.lead_name,
      lead_address: task.lead_address,
      lead_opening_hours: task.lead_opening_hours,
      task_type: outcomeConfig.nextTask,
      typ: outcomeConfig.nextTask,
      due_date: nextDate.toISOString().split('T')[0],
      status: 'Offen',
      info_block: `Wiedervorlage: ${outcomeConfig.label}${notes ? ` - ${notes}` : ''}`,
      assigned_agent: task.assigned_agent,
      previous_task_id: task.id,
    });

    // Notify agent about new follow-up task
    if (task.assigned_agent) {
      notifyTaskAssigned(newTask, task.assigned_agent);
    }

    return newTask;
  }

  return null;
}

/**
 * Sonraki tarihi hesaplar
 * @param {number} days - Gün sayısı
 * @returns {Date} Hesaplanan tarih
 */
function calculateNextDate(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);

  // Hafta sonu kontrolü - Pazartesi'ye al
  const dayOfWeek = date.getDay();
  if (dayOfWeek === 0) date.setDate(date.getDate() + 1); // Pazar -> Pazartesi
  if (dayOfWeek === 6) date.setDate(date.getDate() + 2); // Cumartesi -> Pazartesi

  return date;
}

/**
 * Lead durumunu görev sonucuna göre günceller (Vertriebsprozess).
 * @param {string} leadId - Lead ID
 * @param {string} outcome - Görev sonucu
 * @param {string} [taskType] - Görev tipi (Erstbesuch özel kuralı için)
 */
export async function updateLeadStatusByOutcome(leadId, outcome, taskType = null) {
  const effectiveType = normalizeTaskType(taskType);

  // Vertriebsprozess Schritt 1: Erstbesuch erfolgreich abgeschlossen
  // → Kontakt automatisch auf "Interessenten".
  if (effectiveType === 'Erstbesuch' && outcome === 'erstbesuch_done') {
    await base44.entities.Lead.update(leadId, { status: 'Interessenten' });
    return;
  }

  const statusMap = {
    'interested': 'Interessenten',
    'order': 'Kunde',
    'accepted': 'Kunde',
    // Rückmeldung: positive → weiterhin Interessenten (Bedarfsabfrage-Status entfällt)
    'positive': 'Interessenten',
    // Rückmeldung: nicht interessiert → Verlorene Kunden, keine Folge-To-dos
    'not_interested': 'Verlorene Kunden',
    'rejected': 'Verlorene Kunden',
    'lost_customer': 'Verlorene Kunden',
  };

  const newStatus = statusMap[outcome];
  if (newStatus) {
    await base44.entities.Lead.update(leadId, { status: newStatus });
  }
}

/**
 * Vertriebsprozess Schritt 2: Angebot versendet.
 * Lead-Status → "Interessenten" + Wiedervorlage: To-do
 * "Nachfassen / Rückmeldeanfrage" nach 2 Tagen.
 *
 * @param {Object} lead - Kontakt
 * @param {string} agentEmail - Zuständiger Agent
 * @returns {Promise<Object|null>} erstelltes Nachfassen-To-do
 */
export async function onAngebotVersendet(lead, agentEmail) {
  if (!lead?.id) return null;

  await base44.entities.Lead.update(lead.id, { status: 'Interessenten' });

  const dueDate = calculateNextDate(2);
  const newTask = await base44.entities.Task.create({
    lead_id: lead.id,
    lead_name: lead.name,
    lead_address: [lead.strasse, lead.plz, lead.ort].filter(Boolean).join(', '),
    task_type: 'Nachfassen',
    typ: 'Nachfassen',
    beschreibung: 'Nachfassen / Rückmeldeanfrage',
    due_date: dueDate.toISOString().split('T')[0],
    datum: dueDate.toISOString().split('T')[0],
    status: 'Offen',
    info_block: 'Wiedervorlage: Angebot versendet — Rückmeldung einholen',
    assigned_agent: agentEmail || lead.assigned_agent || null,
  });

  if (newTask && (agentEmail || lead.assigned_agent)) {
    notifyTaskAssigned(newTask, agentEmail || lead.assigned_agent);
  }
  return newTask;
}

/**
 * Görev önceliğini hesaplar
 * @param {Object} task - Görev objesi
 * @returns {number} Öncelik skoru (yüksek = daha öncelikli)
 */
export function calculateTaskPriority(task) {
  let priority = 0;
  if (!task.due_date) return priority;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(task.due_date + 'T00:00:00');
  const daysUntilDue = Math.round((dueDate - today) / (1000 * 60 * 60 * 24));

  // Gecikmiş görevler en yüksek öncelik
  if (daysUntilDue < 0) {
    priority += 100 + Math.abs(daysUntilDue) * 10;
  }
  // Bugün yapılacaklar
  else if (daysUntilDue === 0) {
    priority += 80;
  }
  // Yaklaşan görevler
  else if (daysUntilDue <= 3) {
    priority += 60 - daysUntilDue * 10;
  }

  // Görev tipine göre bonus
  const typeBonus = {
    'Reklamation': 30,
    'Lieferung': 20,
    'Angebot': 15,
    'Bedarfsabfrage': 15,
    'Besuchen': 10,
    'Nachfassen': 5,
    'Anrufen': 5,
  };
  priority += typeBonus[task.task_type] || 0;

  return priority;
}

/**
 * Görevleri önceliğe göre sıralar
 * @param {Array} tasks - Görev listesi
 * @returns {Array} Sıralanmış görevler
 */
export function sortTasksByPriority(tasks) {
  return [...tasks].sort((a, b) => calculateTaskPriority(b) - calculateTaskPriority(a));
}

export default {
  TASK_TYPES,
  TASK_OUTCOMES,
  completeTask,
  updateLeadStatusByOutcome,
  onAngebotVersendet,
  calculateTaskPriority,
  sortTasksByPriority,
};

/**
 * "Lieferung" görevlerini OTOMATİK tamamlar — sipariş operasyonel olarak
 * ilerlediğinde (Kommissioniert + Lieferschein üretildi veya Admin
 * siparişi Abgeschlossen yaptı) çağrılır. Sonuç metni göreve VE
 * bildirime yazılır; agent elle kapatmak zorunda kalmaz.
 *
 * @param {{ leadId: string, resultText: string, actorEmail?: string }} args
 * @returns {Promise<number>} tamamlanan görev sayısı
 */
export async function completeLieferungTasksForLead({ leadId, resultText, actorEmail = 'System' }) {
  if (!leadId) return 0;
  let tasks = [];
  try {
    tasks = await base44.entities.Task.filter({ lead_id: leadId, status: 'Offen' });
  } catch {
    return 0;
  }
  const lieferungTasks = (tasks || []).filter(
    (t) => (t.task_type || t.typ) === 'Lieferung'
  );

  let done = 0;
  for (const task of lieferungTasks) {
    try {
      await base44.entities.Task.update(task.id, {
        status: 'Abgeschlossen',
        action_result: 'auto_completed',
        ergebnis: resultText,
        notes: resultText,
        completed_date: new Date().toISOString(),
      });
      done++;
      // Bildirimde NE olduğu görünür (Ergebnis dahil)
      notifyTaskCompleted(
        { ...task, ergebnis: resultText },
        actorEmail, 'System',
        { ergebnis: resultText }
      );
    } catch (err) {
      console.warn('[TaskStateMachine] Lieferung-Task auto-complete failed:', err);
    }
  }
  return done;
}
